import type {
    LeadCategory,
    LeadStreamFilterExample,
    LeadStreamFilterProfile,
    Stream,
} from 'src/services/LeadStreamsService/agent';
import type { ILeadExample } from 'src/services/NicheAnalysisService';

export type LeadExampleChoiceValue = 'fit' | 'skip' | 'maybe';

export const LEAD_STREAM_FILTER_PROFILE_VERSION = 'lead_stream_filter_v2';

export const DEFAULT_LEAD_STREAM_TARGET_INTENTS = [
    'ищет исполнителя, специалиста, компанию или сервис',
    'просит контакты, рекомендации или личный опыт',
    'спрашивает цену, сроки, условия, консультацию или запись',
    'описывает задачу или проблему и готов выбрать решение',
];

export const DEFAULT_LEAD_STREAM_EXCLUDE_RULES = [
    'запрос относится к другой услуге, продукту или категории',
    'продавцы и подрядчики предлагают свои услуги',
    'реклама и самопродвижение',
    'вакансии, поиск работы и резюме',
    'общие обсуждения без явной заявки',
    'спам и нерелевантные сообщения',
];

const SKIP_REASON_TO_RULE: Record<string, string> = {
    'Не то гео': 'сообщение явно относится к другому гео',
    'Не та услуга': 'запрос относится к другой услуге или продукту',
    'Спам или реклама': 'спам, реклама или самопродвижение',
    'Слишком общий запрос': 'слишком общий вопрос без готовности выбрать исполнителя',
};

const cleanText = (value?: string | null, limit = 260) => {
    const normalized = (value || '').replace(/\s+/g, ' ').trim();
    if (normalized.length <= limit) {
        return normalized;
    }
    return `${normalized.slice(0, limit - 1).trim()}…`;
};

const uniqueTexts = (items: Array<string | undefined | null>, limit = 10) => {
    const seen = new Set<string>();
    const result: string[] = [];

    items.forEach((item) => {
        const text = cleanText(item, 180);
        const key = text.toLowerCase();
        if (!text || seen.has(key) || result.length >= limit) {
            return;
        }
        seen.add(key);
        result.push(text);
    });

    return result;
};

const toFilterExample = (example: ILeadExample, reason?: string): LeadStreamFilterExample => ({
    id: example.id,
    message_id: example.id,
    text: cleanText(example.text),
    ...(reason ? { reason: cleanText(reason, 140) } : {}),
});

export function buildLeadStreamFilterProfile({
    businessDescription,
    geo,
    category,
    examples,
    choices,
    skipReasons,
}: {
    businessDescription: string;
    geo: string;
    category?: LeadCategory | null;
    examples: ILeadExample[];
    choices: Record<number, LeadExampleChoiceValue>;
    skipReasons: Record<string, string>;
}): LeadStreamFilterProfile {
    const fitExamples = examples.filter((example) => choices[example.id] === 'fit').slice(0, 8);
    const skippedExamples = examples.filter((example) => choices[example.id] === 'skip').slice(0, 8);
    const maybeExamples = examples.filter((example) => choices[example.id] === 'maybe').slice(0, 4);
    const skipReasonRules = skippedExamples.map((example) => {
        const reason = skipReasons[String(example.id)];
        return SKIP_REASON_TO_RULE[reason] || reason;
    });

    return {
        schema_version: LEAD_STREAM_FILTER_PROFILE_VERSION,
        source: 'leadsmania2_launch_review',
        customer_description: cleanText(businessDescription, 800),
        offer: cleanText(businessDescription, 300),
        geo: cleanText(geo, 120) || 'Локация не указана',
        category: category
            ? {
                  id: category.id,
                  name: category.name,
                  description: category.description,
              }
            : undefined,
        strict_category_match: true,
        target_intents: DEFAULT_LEAD_STREAM_TARGET_INTENTS,
        exclude: uniqueTexts([...DEFAULT_LEAD_STREAM_EXCLUDE_RULES, ...skipReasonRules]),
        positive_examples: fitExamples.map((example) => toFilterExample(example)),
        negative_examples: skippedExamples.map((example) =>
            toFilterExample(example, skipReasons[String(example.id)] || 'Пользователь отметил: не подходит')
        ),
        maybe_examples: maybeExamples.map((example) => toFilterExample(example)),
        feedback_counters: {
            good: 0,
            bad: 0,
        },
        feedback_revision: 0,
    };
}

const formatList = (items: string[] = [], fallback: string) =>
    items.length ? items.map((item) => `- ${item}`).join('\n') : `- ${fallback}`;

const formatExamples = (examples: LeadStreamFilterExample[] = [], fallback: string) =>
    examples.length
        ? examples.map((example, index) => `${index + 1}. "${cleanText(example.text)}"`).join('\n')
        : `- ${fallback}`;

export function compileLeadStreamPromptPreview(profile: LeadStreamFilterProfile) {
    return [
        `Описание клиента: ${profile.customer_description || profile.offer || 'не указано'}.`,
        `Ищи лиды для: ${profile.offer || 'не указано'}.`,
        `Гео: ${profile.geo || 'Локация не указана'}.`,
        profile.category?.name ? `Категория потока: ${profile.category.name}.` : '',
        profile.category?.description ? `Границы категории: ${profile.category.description}.` : '',
        'Запросы из другой категории не считать лидами.',
        'Считать лидом:',
        formatList(profile.target_intents, 'есть явный спрос на услугу или продукт'),
        'Не считать лидом:',
        formatList(profile.exclude, 'нет явной потребности или запрос не относится к услуге'),
        'Пользователь отметил как целевые примеры:',
        formatExamples(profile.positive_examples, 'пока нет примеров'),
        'Пользователь отметил как нецелевые примеры:',
        formatExamples(profile.negative_examples, 'пока нет примеров'),
        'Если сообщение подходит только частично, лучше не засчитывать его как лид.',
    ]
        .filter(Boolean)
        .join('\n');
}

function parseLegacyPrompt(prompt?: string | null): Partial<LeadStreamFilterProfile> {
    const lines = (prompt || '').split('\n').map((line) => line.trim());
    const offerLine = lines.find(
        (line) => /^Ищи лиды для:/i.test(line) || /^Бизнес:/i.test(line) || /^Услуга или продукт:/i.test(line)
    );
    const geoLine = lines.find((line) => /^Гео:/i.test(line) || /^Гео не ограничивать/i.test(line));
    const extractRules = (headings: RegExp[]) => {
        const result: string[] = [];
        let isCollecting = false;
        const stopHeadings = [
            /^Гео:/i,
            /^Категория потока:/i,
            /^Считать лидом/i,
            /^Засчитывай как лид/i,
            /^Не считать лидом/i,
            /^Не засчитывай/i,
            /^Пользователь отметил/i,
            /^Позитивные примеры/i,
            /^Негативные примеры/i,
            /^Сомнительные примеры/i,
            /^Если сообщение/i,
            /^Правила решения/i,
            /^Верни строго/i,
            /^Ответ в JSON/i,
            /^Анализируемое сообщение/i,
        ];

        lines.forEach((line) => {
            if (headings.some((heading) => heading.test(line))) {
                isCollecting = true;
                return;
            }

            if (!isCollecting) {
                return;
            }

            if (stopHeadings.some((heading) => heading.test(line))) {
                isCollecting = false;
                return;
            }

            const rule = cleanText(line.replace(/^[-•*]\s*/, '').replace(/^\d+\.\s*/, ''), 180);
            if (rule) {
                result.push(rule);
            }
        });

        return uniqueTexts(result, 10);
    };

    const offer = offerLine
        ? offerLine
              .replace(/^Ищи лиды для:/i, '')
              .replace(/^Бизнес:/i, '')
              .replace(/^Услуга или продукт:/i, '')
              .replace(/[.\s]+$/g, '')
              .trim()
        : undefined;
    const geo = /^Гео не ограничивать/i.test(geoLine || '')
        ? 'Локация не важна'
        : geoLine
        ? geoLine
              .replace(/^Гео:/i, '')
              .replace(/[.\s]+$/g, '')
              .trim()
        : undefined;

    return {
        offer,
        geo,
        target_intents: extractRules([/^Считать лидом/i, /^Засчитывай как лид/i]),
        exclude: extractRules([/^Не считать лидом/i, /^Не засчитывай/i]),
    };
}

export function getLeadStreamFilterProfileSummary(stream: Stream) {
    const profile = stream.filter_profile || {};
    const legacy = parseLegacyPrompt(stream.prompt);
    const isStructured = Boolean(stream.filter_profile && Object.keys(stream.filter_profile).length);
    const offer = cleanText(profile.offer || legacy.offer || '', 180);
    const geo = cleanText(profile.geo || legacy.geo || '', 120);
    const targetIntents = uniqueTexts(
        profile.target_intents || legacy.target_intents || (isStructured ? DEFAULT_LEAD_STREAM_TARGET_INTENTS : []),
        6
    );
    const exclude = uniqueTexts(
        profile.exclude || legacy.exclude || (isStructured ? DEFAULT_LEAD_STREAM_EXCLUDE_RULES : []),
        6
    );
    const positiveExamplesCount = profile.positive_examples?.length || 0;
    const negativeExamplesCount = profile.negative_examples?.length || 0;
    const feedbackCount = (profile.feedback_counters?.good || 0) + (profile.feedback_counters?.bad || 0);

    return {
        offer,
        geo,
        targetIntents,
        exclude,
        positiveExamplesCount,
        negativeExamplesCount,
        feedbackCount,
        isStructured,
    };
}
